
import { useState, useEffect } from 'react';

export type DebugInfo = {
  location: string | null;
  date: string | null;
  condition: string | null;
  forecastDuration: number | null;
  customQuote: string | null;
  screenSize: string;
  userAgent: string;
  timestamp: number;
}

export const useDebug = () => {
  const [isDebugMode, setIsDebugMode] = useState<boolean>(false);
  
  useEffect(() => {
    // Check if debug mode is enabled in localStorage
    const debugEnabled = localStorage.getItem('meteorwish_debug') === 'true';
    setIsDebugMode(debugEnabled);
    
    // Handle keyboard shortcut (Ctrl+Shift+D) to toggle debug mode
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        toggleDebugMode();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  const toggleDebugMode = () => {
    const newState = !isDebugMode;
    setIsDebugMode(newState);
    localStorage.setItem('meteorwish_debug', newState ? 'true' : 'false');
    console.log(`Debug mode ${newState ? 'enabled' : 'disabled'}`);
  };
  
  const logDebugInfo = (info: Partial<DebugInfo>) => {
    if (!isDebugMode) return;
    
    const debugInfo: DebugInfo = {
      location: info.location || null,
      date: info.date || null,
      condition: info.condition || null,
      forecastDuration: info.forecastDuration || null,
      customQuote: info.customQuote || null,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
      ...info
    };
    
    console.group('🔍 MeteorWish Debug Info');
    console.table(debugInfo);
    console.groupEnd();
    
    return debugInfo;
  };
  
  return {
    isDebugMode,
    toggleDebugMode,
    logDebugInfo
  };
};
